import { makeId } from "./runner"
import type {
  CaseExpectations,
  JsonAssertion,
  JudgeAssertion,
  PlaygroundMessage,
  PromptTestCase,
  PromptTestSuite,
  SuiteDefaults
} from "./types"

/**
 * Paste-able spec for generating a suite with an LLM. Kept in sync with
 * `parseSuiteJson` below — every field documented here is actually honored.
 */
export const SUITE_SPEC_MARKDOWN = `# Qpien Prompt Test Suite — JSON format (v1)

Aşağıdaki formatta, TEK bir JSON objesi üret. Açıklama/markdown ekleme, sadece JSON döndür.

\`\`\`jsonc
{
  "version": 1,
  "promptName": "<PROMPT_ADI>",
  "description": "Bu suite neyi test ediyor",
  "defaults": {
    "variables": { "language": "tr" },   // tüm case'lere uygulanır, case kendi değerini ezebilir
    "conditions": { "commerce": true },  // {{#if_commerce}}…{{/if_commerce}} bloklarını aç/kapat
    "temperature": 0.1,
    "maxTokens": 1024,
    "timeoutMs": 60000
  },
  "cases": [
    {
      "id": "unique-case-id",
      "name": "İnsan tarafından okunur başlık",
      "tags": ["smoke", "tr"],
      "skip": false,
      "query": "Kullanıcının gönderdiği mesaj",
      "variables": { "knowledgeSource": "..." },   // prompttaki {{placeholder}} değerleri
      "conditions": { "rag": true, "action": false },  // koşullu blok anahtarları (kapalıysa blok silinir)
      "previousMessages": [
        { "role": "user", "content": "önceki mesaj" },
        { "role": "assistant", "content": "önceki cevap" }
      ],
      "expect": {
        "contains": ["kargo"],              // çıktı bu metinleri içermeli
        "notContains": ["üzgünüm"],         // içermemeli
        "regex": "^\\\\{.*\\\\}$",              // çıktı bu regex'e uymalı
        "caseSensitive": false,             // contains/notContains/regex için (varsayılan false)
        "json": [                            // responseSchema'lı promptlar için
          { "path": "needRag", "equals": true },
          { "path": "items[0].name", "exists": true },
          { "path": "intent", "oneOf": ["rag", "action"] },
          { "path": "answer", "contains": "kargo" }
        ],
        "maxLatencyMs": 8000,
        "judge": {                           // LLM-as-judge (opsiyonel, ekstra maliyet)
          "criteria": "Cevap Türkçe, kibar ve soruyu doğrudan yanıtlıyor olmalı",
          "minScore": 7
        }
      }
    }
  ]
}
\`\`\`

Kurallar:
- \`id\` benzersiz olmalı, \`query\` zorunlu.
- \`expect\` içindeki tüm kontroller AND'lenir; hepsi geçerse case PASS.
- \`expect\` yoksa case sadece "hata vermeden çalıştı mı" diye kontrol edilir.
- \`json\` yolları nokta/bracket notasyonu kullanır: \`a.b[0].c\`.
`

export function buildSuiteTemplate(
  promptName: string,
  variableNames: string[],
  conditionNames: string[] = []
): PromptTestSuite {
  const variables = variableNames.reduce<Record<string, string>>(
    (acc, name) => ({ ...acc, [name]: "" }),
    {}
  )
  const conditions = conditionNames.reduce<Record<string, boolean>>(
    (acc, name) => ({ ...acc, [name]: true }),
    {}
  )
  const hasDefaults =
    Object.keys(variables).length > 0 || Object.keys(conditions).length > 0

  return {
    version: 1,
    promptName,
    description: `${promptName} için hazır soru seti`,
    defaults: hasDefaults
      ? {
          variables: Object.keys(variables).length ? variables : undefined,
          conditions: Object.keys(conditions).length ? conditions : undefined
        }
      : undefined,
    cases: [
      {
        id: "ornek-1",
        name: "Örnek: temel soru",
        tags: ["smoke"],
        query: "Kargom nerede?",
        expect: {
          contains: [],
          notContains: [],
          maxLatencyMs: 15000
        }
      }
    ]
  }
}

export interface ParsedSuite {
  suite?: PromptTestSuite
  errors: string[]
  warnings: string[]
}

type Dict = Record<string, unknown>

function isDict(value: unknown): value is Dict {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" ? value : undefined
}

function asBooleanMap(value: unknown): Record<string, boolean> | undefined {
  if (!isDict(value)) return undefined
  return Object.entries(value).reduce<Record<string, boolean>>(
    (acc, [key, flag]) => ({ ...acc, [key]: Boolean(flag) }),
    {}
  )
}

function asStringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) ? value.map((item) => String(item)) : undefined
}

function normalizeMessages(value: unknown): PlaygroundMessage[] | undefined {
  if (!Array.isArray(value)) return undefined
  const messages = value
    .filter(isDict)
    .map((item) => ({
      role: asString(item.role),
      content: asString(item.content)
    }))
    .filter((item): item is PlaygroundMessage => {
      const roleOk =
        item.role === "user" ||
        item.role === "assistant" ||
        item.role === "system"
      return roleOk && typeof item.content === "string"
    })
  return messages.length ? messages : undefined
}

function normalizeExpect(value: unknown): CaseExpectations | undefined {
  if (!isDict(value)) return undefined
  const judge = isDict(value.judge) ? value.judge : undefined
  const judgeCriteria = asString(judge?.criteria)

  return {
    contains: asStringArray(value.contains),
    notContains: asStringArray(value.notContains),
    regex: asString(value.regex),
    caseSensitive: Boolean(value.caseSensitive),
    json: Array.isArray(value.json)
      ? value.json.filter(
          (item): item is JsonAssertion =>
            isDict(item) && typeof item.path === "string"
        )
      : undefined,
    maxLatencyMs: asNumber(value.maxLatencyMs),
    judge: judgeCriteria
      ? {
          criteria: judgeCriteria,
          model: asString(judge?.model) as JudgeAssertion["model"],
          minScore: asNumber(judge?.minScore)
        }
      : undefined
  }
}

function normalizeDefaults(value: unknown): SuiteDefaults | undefined {
  if (!isDict(value)) return undefined
  return {
    variables: isDict(value.variables)
      ? Object.entries(value.variables).reduce<Record<string, string>>(
          (acc, [key, item]) => ({ ...acc, [key]: String(item) }),
          {}
        )
      : undefined,
    conditions: asBooleanMap(value.conditions),
    temperature: asNumber(value.temperature),
    maxTokens: asNumber(value.maxTokens),
    timeoutMs: asNumber(value.timeoutMs)
  }
}

function normalizeCase(
  raw: unknown,
  index: number,
  errors: string[]
): PromptTestCase | null {
  if (!isDict(raw)) {
    errors.push(`cases[${index}]: obje değil`)
    return null
  }

  const query = asString(raw.query)
  if (!query?.trim()) {
    errors.push(`cases[${index}]: query alanı zorunlu`)
    return null
  }

  const variables = isDict(raw.variables)
    ? Object.entries(raw.variables).reduce<Record<string, string>>(
        (acc, [key, value]) => ({ ...acc, [key]: String(value) }),
        {}
      )
    : undefined

  return {
    id: asString(raw.id)?.trim() || makeId("case"),
    name: asString(raw.name)?.trim() || `Case ${index + 1}`,
    query,
    tags: asStringArray(raw.tags),
    skip: Boolean(raw.skip),
    variables,
    conditions: asBooleanMap(raw.conditions),
    previousMessages: normalizeMessages(raw.previousMessages),
    expect: normalizeExpect(raw.expect)
  }
}

export function parseSuiteJson(text: string, promptName: string): ParsedSuite {
  const errors: string[] = []
  const warnings: string[] = []

  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch (error) {
    return { errors: [`Geçersiz JSON: ${String(error)}`], warnings }
  }

  const root = isDict(raw) ? raw : {}
  const rawCases = Array.isArray(raw) ? raw : root.cases
  if (!Array.isArray(rawCases)) {
    return { errors: ["cases dizisi bulunamadı"], warnings }
  }

  const filePromptName = asString(root.promptName)
  if (filePromptName && filePromptName !== promptName) {
    warnings.push(
      `Dosya "${filePromptName}" için üretilmiş, "${promptName}" promptuna yükleniyor.`
    )
  }

  const cases = rawCases
    .map((item, index) => normalizeCase(item, index, errors))
    .filter((item): item is PromptTestCase => item !== null)

  const seen = new Set<string>()
  for (const testCase of cases) {
    if (seen.has(testCase.id)) {
      testCase.id = makeId("case")
      warnings.push(`Tekrarlanan id yeniden üretildi: ${testCase.name}`)
    }
    seen.add(testCase.id)
  }

  if (!cases.length) {
    errors.push("Geçerli case bulunamadı")
  }

  if (errors.length) return { errors, warnings }

  return {
    warnings,
    errors,
    suite: {
      version: 1,
      promptName,
      description: asString(root.description),
      defaults: normalizeDefaults(root.defaults),
      cases
    }
  }
}

export function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json"
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}
